import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import TodayIcon from "@mui/icons-material/Today";
import SyncAltRoundedIcon from "@mui/icons-material/SyncAltRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../axiosConfig";
import { AuthContext } from "../AuthContext";
import { listLines } from "../services/whatsappLinesApi";

const CARD_SHADOW = "0 2px 12px rgba(0,0,0,0.07)";
const BORDER = "1px solid rgba(148,163,184,0.18)";

function avatarColor(name = "") {
  const colors = ["#4f46e5", "#0ea5e9", "#16a34a", "#b45309", "#7c3aed", "#e11d48", "#0891b2"];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function initials(name = "") {
  return String(name || "?")
    .split(" ")
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
}

function mondayOfWeek(dateStr) {
  const d = new Date(dateStr);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function formatShort(dateStr) {
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}

function resolveFallbackPath(user) {
  const role = String(user?.role || "").toLowerCase();
  if (role === "superadmin") return "/superadmin";
  if (user?.is_admin || role === "admin") return "/admin";
  return "/omnichannel/inbox";
}

function handoverStatusMeta(status = "") {
  switch (String(status || "").toLowerCase()) {
    case "completed":
      return { label: "Completed", color: "#15803d", bg: "#dcfce7" };
    case "acknowledged":
      return { label: "Acknowledged", color: "#1d4ed8", bg: "#dbeafe" };
    default:
      return { label: "Pending acknowledgment", color: "#b45309", bg: "#fef3c7" };
  }
}

function MemberRow({ member, index, total, canEdit, onMoveUp, onMoveDown, onRemove }) {
  return (
    <Paper
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1.5,
        px: { xs: 1.5, sm: 2 },
        py: 1.25,
        borderRadius: "14px",
        border: BORDER,
        boxShadow: "none",
        bgcolor: "rgba(255,255,255,0.9)",
      }}
    >
      <Typography
        sx={{
          fontWeight: 900,
          fontSize: "0.8rem",
          color: "text.secondary",
          minWidth: 22,
          textAlign: "center",
        }}
      >
        {index + 1}
      </Typography>

      <Avatar sx={{ width: 36, height: 36, bgcolor: avatarColor(member.name), fontSize: "0.8rem" }}>
        {initials(member.name)}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", overflowWrap: "anywhere" }}>{member.name}</Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", overflowWrap: "anywhere" }}>
          {member.role ? member.role.charAt(0).toUpperCase() + member.role.slice(1) : ""}
          {member.whatsapp_number ? ` · ${member.whatsapp_number}` : " · No WhatsApp linked"}
        </Typography>
      </Box>

      {!member.whatsapp_number && (
        <Tooltip title="No WhatsApp number linked — reminders and handover alerts will not reach this member">
          <Chip
            label="No WhatsApp"
            size="small"
            sx={{ bgcolor: "#fef3c7", color: "#b45309", fontWeight: 700, fontSize: "0.7rem" }}
          />
        </Tooltip>
      )}

      {canEdit && (
        <Stack direction="row" spacing={0.25} sx={{ width: { xs: "100%", sm: "auto" }, justifyContent: { xs: "flex-end", sm: "flex-start" } }}>
          <IconButton size="small" onClick={onMoveUp} disabled={index === 0} sx={{ opacity: index === 0 ? 0.3 : 1 }}>
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onMoveDown} disabled={index === total - 1} sx={{ opacity: index === total - 1 ? 0.3 : 1 }}>
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={onRemove}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      )}
    </Paper>
  );
}

function WeekRow({ row, position }) {
  const isCurrent = row.is_current;
  const isPast = !isCurrent && new Date(row.week_end) < new Date();
  const user = row.user;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "60px minmax(0, 1.15fr) minmax(0, 0.95fr) minmax(0, 0.95fr)" },
        alignItems: { xs: "flex-start", sm: "center" },
        gap: { xs: 1.1, sm: 1 },
        px: { xs: 1.5, sm: 2 },
        py: isCurrent ? 1.5 : 1,
        borderRadius: "12px",
        bgcolor: isCurrent
          ? "rgba(79,70,229,0.07)"
          : isPast
          ? "transparent"
          : "rgba(255,255,255,0.6)",
        border: isCurrent ? "1.5px solid rgba(79,70,229,0.30)" : "1px solid transparent",
        opacity: isPast ? 0.55 : 1,
      }}
    >
      <Box sx={{ textAlign: { xs: "left", sm: "center" } }}>
        {isCurrent ? (
          <Chip
            label="NOW"
            size="small"
            sx={{ bgcolor: "#4f46e5", color: "#fff", fontWeight: 900, fontSize: "0.68rem" }}
          />
        ) : (
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
            Wk {position}
          </Typography>
        )}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: isCurrent ? 800 : 600, fontSize: "0.87rem", overflowWrap: "anywhere" }}>
          {formatShort(row.week_start)} 08:00 – {formatShort(row.week_end)} 08:00
        </Typography>
        {isCurrent && (
          <Typography variant="caption" sx={{ color: "#4f46e5", fontWeight: 700 }}>
            Current duty week
          </Typography>
        )}
      </Box>

      {user ? (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <Avatar sx={{ width: 28, height: 28, bgcolor: avatarColor(user.name), fontSize: "0.7rem" }}>
            {initials(user.name)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: isCurrent ? 800 : 600, fontSize: "0.87rem", overflowWrap: "anywhere" }}>
              {user.name}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.72rem", overflowWrap: "anywhere" }}>
              {user.role}
            </Typography>
          </Box>
        </Stack>
      ) : (
        <Typography variant="caption" sx={{ color: "error.main" }}>
          Unknown member
        </Typography>
      )}

      <Box sx={{ textAlign: { xs: "left", sm: "right" }, minWidth: 0 }}>
        {user?.whatsapp_number ? (
          <Chip
            icon={<NotificationsActiveIcon sx={{ fontSize: "0.75rem !important" }} />}
            label={user.whatsapp_number}
            size="small"
            sx={{
              maxWidth: "100%",
              fontSize: "0.68rem",
              fontWeight: 600,
              bgcolor: "#dcfce7",
              color: "#15803d",
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        ) : (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            No WhatsApp
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function SummaryPerson({ title, user, accent }) {
  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: "16px",
        border: BORDER,
        boxShadow: "none",
        bgcolor: "rgba(255,255,255,0.85)",
      }}
    >
      <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.4px" }}>
        {title}
      </Typography>
      {user ? (
        <Stack direction="row" spacing={1.25} alignItems="center" mt={1}>
          <Avatar sx={{ width: 38, height: 38, bgcolor: accent || avatarColor(user.name), fontSize: "0.8rem" }}>
            {initials(user.name)}
          </Avatar>
          <Box minWidth={0}>
            <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", overflowWrap: "anywhere" }}>
              {user.name}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", overflowWrap: "anywhere" }}>
              {user.email || "No email"}
            </Typography>
          </Box>
        </Stack>
      ) : (
        <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
          Not set
        </Typography>
      )}
    </Paper>
  );
}

export default function PhoneRotationsPage() {
  const { user: currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const canEdit = useMemo(() => {
    const role = String(currentUser?.role || "").toLowerCase();
    return currentUser?.is_admin || ["admin", "superadmin"].includes(role);
  }, [currentUser]);

  const fallbackPath = useMemo(() => resolveFallbackPath(currentUser), [currentUser]);
  const returnPath = location.state?.from || fallbackPath;

  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);
  const [handoverSaving, setHandoverSaving] = useState(false);
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState("");

  const [label, setLabel] = useState("Phone Duty Rotation");
  const [mainLineNumber, setMainLineNumber] = useState("");
  const [rotationLineId, setRotationLineId] = useState("");
  const [startDate, setStartDate] = useState(() => {
    const d = mondayOfWeek(new Date().toISOString().slice(0, 10));
    return d.toISOString().slice(0, 10);
  });
  const [rotationMembers, setRotationMembers] = useState([]);

  const [allUsers, setAllUsers] = useState([]);
  const [rotatingLines, setRotatingLines] = useState([]);
  const [addUserId, setAddUserId] = useState("");

  const [schedule, setSchedule] = useState([]);
  const [hasConfig, setHasConfig] = useState(false);
  const [handoverSummary, setHandoverSummary] = useState({ rotation: null, handover: null });

  const loadData = useCallback(async () => {
    setLoadingData(true);
    setError("");

    try {
      const [rotationRes, handoverRes, usersRes, linesRes] = await Promise.all([
        api.get("/phone-rotation"),
        api.get("/phone-rotation/current-handover"),
        canEdit ? api.get("/admin/users") : Promise.resolve({ data: [] }),
        canEdit ? listLines({ line_type: "rotated_work" }).catch(() => []) : Promise.resolve([]),
      ]);

      const data = rotationRes.data?.data;
      const handoverData = handoverRes.data?.data ?? { rotation: null, handover: null };

      setHandoverSummary(handoverData);
      setRotatingLines(Array.isArray(linesRes) ? linesRes : []);

      if (data?.config) {
        setHasConfig(true);
        setLabel(data.config.label || "Phone Duty Rotation");
        setRotationLineId(data.config.whatsapp_line_id ? String(data.config.whatsapp_line_id) : "");
        setMainLineNumber(data.config.main_line_whatsapp_number || data.config.whatsapp_line?.phone_number || "");
        setStartDate(data.config.rotation_start_date);
        setRotationMembers(data.members || []);
        setSchedule(data.schedule || []);
      } else {
        setHasConfig(false);
        setLabel("Phone Duty Rotation");
        setRotationLineId("");
        setMainLineNumber("");
        setRotationMembers([]);
        setSchedule([]);
      }

      const rawUsers = Array.isArray(usersRes.data)
        ? usersRes.data
        : (usersRes.data?.users || usersRes.data?.data || []);
      setAllUsers(rawUsers);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Failed to load rotation data.");
    } finally {
      setLoadingData(false);
    }
  }, [canEdit]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const selectedRotationLine = useMemo(() => {
    const selected = rotatingLines.find((line) => String(line.id) === String(rotationLineId));
    if (selected) {
      return selected;
    }

    const summaryLine = handoverSummary?.rotation?.line;
    if (summaryLine && (!rotationLineId || String(summaryLine.id) === String(rotationLineId))) {
      return summaryLine;
    }

    return null;
  }, [handoverSummary, rotatingLines, rotationLineId]);

  useEffect(() => {
    if (rotationLineId && selectedRotationLine?.phone_number) {
      setMainLineNumber(selectedRotationLine.phone_number);
    }
  }, [rotationLineId, selectedRotationLine]);

  const moveUp = (index) => {
    if (index === 0) return;
    const next = [...rotationMembers];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    setRotationMembers(next);
  };

  const moveDown = (index) => {
    if (index === rotationMembers.length - 1) return;
    const next = [...rotationMembers];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    setRotationMembers(next);
  };

  const removeMember = (index) => {
    setRotationMembers((prev) => prev.filter((_, i) => i !== index));
  };

  const addMember = () => {
    if (!addUserId) return;
    const user = allUsers.find((entry) => String(entry.id) === String(addUserId));
    if (!user) return;
    if (rotationMembers.some((member) => member.id === user.id)) {
      setSnackbar("That member is already in the rotation.");
      return;
    }
    setRotationMembers((prev) => [...prev, user]);
    setAddUserId("");
  };

  const handleSave = async () => {
    if (!startDate) {
      setError("Please set a start date.");
      return;
    }
    if (rotationMembers.length === 0) {
      setError("Add at least one member to the rotation.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const resolvedNumber = rotationLineId
        ? (selectedRotationLine?.phone_number || mainLineNumber.trim() || null)
        : (mainLineNumber.trim() || null);

      await api.put("/phone-rotation", {
        label,
        whatsapp_line_id: rotationLineId ? Number(rotationLineId) : null,
        main_line_whatsapp_number: resolvedNumber,
        rotation_start_date: startDate,
        member_ids: rotationMembers.map((member) => member.id),
      });

      setSnackbar("Rotation saved. Duty schedule and linked line updated.");
      setHasConfig(true);
      await loadData();
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Failed to save rotation.");
    } finally {
      setSaving(false);
    }
  };

  const handleHandoverAction = async (action) => {
    const handoverId = handoverSummary?.handover?.id;
    if (!handoverId) return;

    setHandoverSaving(true);
    setError("");

    try {
      await api.post(`/phone-rotation/handover/${action}`, {
        handover_id: handoverId,
      });
      setSnackbar(action === "acknowledge" ? "Takeover acknowledged." : "Handover marked complete.");
      await loadData();
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Failed to update the rotating handover.");
    } finally {
      setHandoverSaving(false);
    }
  };

  const handleStartHandover = async () => {
    setHandoverSaving(true);
    setError("");

    try {
      await api.post("/phone-rotation/handover/start");
      setSnackbar("Rotating handover refreshed.");
      await loadData();
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Failed to start the rotating handover.");
    } finally {
      setHandoverSaving(false);
    }
  };

  const availableToAdd = useMemo(
    () => allUsers.filter((entry) => !rotationMembers.some((member) => member.id === entry.id)),
    [allUsers, rotationMembers]
  );

  const currentWeekRow = schedule.find((row) => row.is_current);
  const activeRotation = handoverSummary?.rotation;
  const activeHandover = handoverSummary?.handover;
  const handoverMeta = handoverStatusMeta(activeHandover?.status);
  const canManageHandover = Boolean(activeHandover)
    && (canEdit || String(currentUser?.id || "") === String(activeHandover?.incoming_user?.id || ""));
  const linkedLineWarning = rotationLineId
    && selectedRotationLine
    && Object.prototype.hasOwnProperty.call(selectedRotationLine, "connection_ready")
    && (!selectedRotationLine.is_active || !selectedRotationLine.connection_ready);

  if (loadingData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f5f7fb",
        pb: 6,
        "& .MuiChip-root": {
          cursor: "default",
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          py: 2.5,
          bgcolor: "#fff",
          borderBottom: BORDER,
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <IconButton onClick={() => navigate(returnPath, { replace: true })}>
          <ArrowBackIcon />
        </IconButton>
        <PhoneIcon sx={{ color: "#4f46e5" }} />
        <Box sx={{ minWidth: 0, flex: "1 1 180px" }}>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
            Phone Rotation
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Repeating weekly duty schedule tied to one rotating WhatsApp line
          </Typography>
        </Box>

        <Box
          sx={{
            ml: { xs: 0, md: "auto" },
            width: { xs: "100%", md: "auto" },
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            justifyContent: { xs: "flex-start", md: "flex-end" },
          }}
        >
          {(currentWeekRow?.user || activeRotation?.current_owner) ? (
            <Chip
              avatar={(
                <Avatar sx={{ bgcolor: avatarColor(currentWeekRow?.user?.name || activeRotation?.current_owner?.name), fontSize: "0.65rem" }}>
                  {initials(currentWeekRow?.user?.name || activeRotation?.current_owner?.name)}
                </Avatar>
              )}
              label={`On duty: ${currentWeekRow?.user?.name || activeRotation?.current_owner?.name}`}
              sx={{
                maxWidth: "100%",
                fontWeight: 700,
                bgcolor: "rgba(79,70,229,0.10)",
                color: "#4f46e5",
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          ) : null}

          {(selectedRotationLine?.phone_number || mainLineNumber) ? (
            <Chip
              icon={<PhoneIcon sx={{ fontSize: "0.8rem !important" }} />}
              label={`Rotating line: ${selectedRotationLine?.phone_number || mainLineNumber}`}
              sx={{
                maxWidth: "100%",
                fontWeight: 700,
                bgcolor: "rgba(22,163,74,0.10)",
                color: "#15803d",
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          ) : null}

          {activeHandover ? (
            <Chip
              icon={<SyncAltRoundedIcon sx={{ fontSize: "0.8rem !important" }} />}
              label={`Handover: ${handoverMeta.label}`}
              sx={{
                fontWeight: 700,
                bgcolor: handoverMeta.bg,
                color: handoverMeta.color,
              }}
            />
          ) : null}
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1040, mx: "auto", px: { xs: 2, md: 3 }, pt: 3, display: "flex", flexDirection: "column", gap: 3 }}>
        {error ? (
          <Alert severity="error" onClose={() => setError("")} sx={{ borderRadius: "14px" }}>
            {error}
          </Alert>
        ) : null}

        <Paper sx={{ borderRadius: "20px", overflow: "hidden", boxShadow: CARD_SHADOW, border: BORDER }}>
          <Box sx={{ px: 3, py: 2, borderBottom: BORDER, bgcolor: "rgba(79,70,229,0.04)" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Configure Rotation
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Link the active rotated_work line, set the start date, and control who takes over each week.
            </Typography>
          </Box>

          <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" }, gap: 2 }}>
              <TextField
                label="Rotation label"
                size="small"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                disabled={!canEdit}
                inputProps={{ maxLength: 120 }}
              />
              <TextField
                label="Rotation start date"
                size="small"
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                disabled={!canEdit}
                InputLabelProps={{ shrink: true }}
                helperText="Member 1 is on duty the week containing this date"
              />
              <TextField
                select
                size="small"
                label="Linked rotating line"
                value={rotationLineId}
                onChange={(event) => setRotationLineId(event.target.value)}
                disabled={!canEdit}
                helperText={
                  canEdit
                    ? "Select the rotated_work line that should follow this roster."
                    : "This roster can be linked to a shared rotating WhatsApp line."
                }
              >
                <MenuItem value="">No linked line yet</MenuItem>
                {rotatingLines.map((line) => (
                  <MenuItem key={line.id} value={String(line.id)}>
                    {line.display_name} · {line.phone_number || "No number"}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={rotationLineId ? "Resolved line phone" : "Legacy fallback phone"}
                size="small"
                value={rotationLineId ? (selectedRotationLine?.phone_number || mainLineNumber) : mainLineNumber}
                onChange={(event) => setMainLineNumber(event.target.value)}
                disabled={!canEdit || Boolean(rotationLineId)}
                placeholder="+263..."
                helperText={
                  rotationLineId
                    ? "Pulled from the linked rotating line."
                    : "Legacy fallback only. Linking the actual rotating line is recommended."
                }
              />
            </Box>

            {linkedLineWarning ? (
              <Alert severity="warning">
                The selected rotating line is not fully ready yet. Make sure it is active and has a valid Meta Phone Number ID before relying on handover ownership.
              </Alert>
            ) : null}

            {!rotationLineId ? (
              <Alert severity="info" icon={<LinkRoundedIcon />}>
                Linking the real rotated_work line is what lets the roster drive accountability, handover tracking, and current duty ownership in Unibox.
              </Alert>
            ) : null}

            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.25, color: "text.secondary" }}>
                Rotation order — members cycle in this sequence each week
              </Typography>

              {rotationMembers.length === 0 ? (
                <Paper
                  sx={{
                    p: 3,
                    textAlign: "center",
                    borderRadius: "14px",
                    border: "1.5px dashed rgba(148,163,184,0.4)",
                    bgcolor: "rgba(248,250,252,0.8)",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No members yet — add them below
                  </Typography>
                </Paper>
              ) : (
                <Stack spacing={1}>
                  {rotationMembers.map((member, index) => (
                    <MemberRow
                      key={member.id}
                      member={member}
                      index={index}
                      total={rotationMembers.length}
                      canEdit={canEdit}
                      onMoveUp={() => moveUp(index)}
                      onMoveDown={() => moveDown(index)}
                      onRemove={() => removeMember(index)}
                    />
                  ))}

                  {rotationMembers.length > 1 ? (
                    <Box sx={{ textAlign: "center", py: 0.5 }}>
                      <Typography variant="caption" sx={{ color: "#4f46e5", fontWeight: 700 }}>
                        ↩ repeats from {rotationMembers[0]?.name?.split(" ")[0]}
                      </Typography>
                    </Box>
                  ) : null}
                </Stack>
              )}
            </Box>

            {canEdit ? (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="flex-start">
                <TextField
                  select
                  size="small"
                  label="Add member"
                  value={addUserId}
                  onChange={(event) => setAddUserId(event.target.value)}
                  sx={{ flex: 1 }}
                  disabled={availableToAdd.length === 0}
                  helperText={availableToAdd.length === 0 ? "All users are already in the rotation" : ""}
                >
                  <MenuItem value="">Select a team member…</MenuItem>
                  {availableToAdd.map((entry) => (
                    <MenuItem key={entry.id} value={String(entry.id)}>
                      {entry.name} ({entry.role})
                      {!entry.whatsapp_number ? " ⚠ no WhatsApp" : ""}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="outlined"
                  onClick={addMember}
                  disabled={!addUserId}
                  sx={{ borderRadius: "12px", mt: { xs: 0, sm: 0.2 }, width: { xs: "100%", sm: "auto" } }}
                >
                  Add
                </Button>
              </Stack>
            ) : null}

            {canEdit ? (
              <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" } }}>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving || rotationMembers.length === 0}
                  sx={{ borderRadius: "14px", px: 3, width: { xs: "100%", sm: "auto" }, bgcolor: "#4f46e5", "&:hover": { bgcolor: "#4338ca" } }}
                >
                  {saving ? "Saving…" : "Save Rotation"}
                </Button>
              </Box>
            ) : null}
          </Box>
        </Paper>

        {(activeRotation || activeHandover) ? (
          <Paper sx={{ borderRadius: "20px", overflow: "hidden", boxShadow: CARD_SHADOW, border: BORDER }}>
            <Box sx={{ px: 3, py: 2, borderBottom: BORDER, bgcolor: "rgba(16,185,129,0.05)" }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <SyncAltRoundedIcon sx={{ color: "#059669", fontSize: 20 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Current Handover
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Track who is taking over the rotating phone and whether they have acknowledged it.
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {activeRotation?.line ? (
                  <Chip
                    icon={<PhoneIcon sx={{ fontSize: "0.8rem !important" }} />}
                    label={`${activeRotation.line.display_name} · ${activeRotation.line.phone_number || "No number"}`}
                    sx={{ fontWeight: 700, bgcolor: "rgba(16,185,129,0.10)", color: "#047857" }}
                  />
                ) : null}
                {activeRotation?.duty_window ? (
                  <Chip
                    icon={<TodayIcon sx={{ fontSize: "0.8rem !important" }} />}
                    label={activeRotation.duty_window}
                    sx={{ fontWeight: 700 }}
                  />
                ) : null}
                {activeHandover ? (
                  <Chip
                    label={handoverMeta.label}
                    sx={{ fontWeight: 700, bgcolor: handoverMeta.bg, color: handoverMeta.color }}
                  />
                ) : null}
              </Stack>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
                <SummaryPerson title="Incoming owner" user={activeHandover?.incoming_user || activeRotation?.current_owner} accent="#059669" />
                <SummaryPerson title="Outgoing owner" user={activeHandover?.outgoing_user} accent="#b45309" />
                <SummaryPerson title="Next owner" user={activeRotation?.next_owner} accent="#4f46e5" />
              </Box>

              {activeHandover?.notes ? (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {activeHandover.notes}
                </Typography>
              ) : null}

              {canManageHandover || canEdit ? (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} justifyContent="flex-end">
                  {canEdit ? (
                    <Button
                      variant="text"
                      onClick={() => void handleStartHandover()}
                      disabled={handoverSaving}
                      sx={{ borderRadius: "12px" }}
                    >
                      {handoverSaving ? "Saving…" : (activeHandover ? "Refresh handover" : "Start handover")}
                    </Button>
                  ) : null}
                  {activeHandover?.status === "pending_acknowledgment" ? (
                    <Button
                      variant="outlined"
                      onClick={() => void handleHandoverAction("acknowledge")}
                      disabled={handoverSaving}
                      sx={{ borderRadius: "12px" }}
                    >
                      {handoverSaving ? "Saving…" : "Acknowledge takeover"}
                    </Button>
                  ) : null}
                  {activeHandover?.status !== "completed" ? (
                    <Button
                      variant="contained"
                      onClick={() => void handleHandoverAction("complete")}
                      disabled={handoverSaving}
                      sx={{ borderRadius: "12px", bgcolor: "#059669", "&:hover": { bgcolor: "#047857" } }}
                    >
                      {handoverSaving ? "Saving…" : "Mark handover complete"}
                    </Button>
                  ) : null}
                </Stack>
              ) : null}
            </Box>
          </Paper>
        ) : null}

        {hasConfig && schedule.length > 0 ? (
          <Paper sx={{ borderRadius: "20px", overflow: "hidden", boxShadow: CARD_SHADOW, border: BORDER }}>
            <Box sx={{ px: 3, py: 2, borderBottom: BORDER, bgcolor: "rgba(79,70,229,0.04)" }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TodayIcon sx={{ color: "#4f46e5", fontSize: 20 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Weekly Schedule
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Showing 4 past weeks + 12 upcoming weeks. Repeats automatically.
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Box
              sx={{
                display: { xs: "none", sm: "grid" },
                gridTemplateColumns: "60px 1fr 1fr 1fr",
                gap: 1,
                px: 2,
                py: 1,
                bgcolor: "rgba(248,250,252,0.8)",
                borderBottom: BORDER,
              }}
            >
              {["Week", "Dates", "On Duty", "WhatsApp"].map((header) => (
                <Typography key={header} variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {header}
                </Typography>
              ))}
            </Box>

            <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
              {schedule.map((row, idx) => (
                <WeekRow key={`${row.week_start}-${row.user_id}-${row.week_index}`} row={row} position={idx + 1} />
              ))}
            </Box>

            <Box
              sx={{
                px: 3,
                py: 1.75,
                borderTop: BORDER,
                bgcolor: "rgba(79,70,229,0.03)",
                display: "flex",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 1.25,
              }}
            >
              <NotificationsActiveIcon sx={{ color: "#4f46e5", fontSize: 18 }} />
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Weekly reminders still follow the duty schedule, but the linked rotating line now also drives handover tracking and current duty ownership.
                {selectedRotationLine?.display_name ? ` Active line: ${selectedRotationLine.display_name}.` : ""}
                Members without a WhatsApp number linked will still be skipped for alerts.
              </Typography>
            </Box>
          </Paper>
        ) : null}

        {!hasConfig ? (
          <Paper
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: "20px",
              border: "1.5px dashed rgba(148,163,184,0.35)",
              boxShadow: "none",
            }}
          >
            <PhoneIcon sx={{ fontSize: 48, color: "rgba(148,163,184,0.5)", mb: 1.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "text.secondary" }}>
              No rotation configured yet
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              Set a start date, choose the rotating line, add members in order, then save to generate the schedule and handover state.
            </Typography>
          </Paper>
        ) : null}
      </Box>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4500}
        onClose={() => setSnackbar("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbar("")} severity="success" sx={{ width: "100%" }}>
          {snackbar}
        </Alert>
      </Snackbar>
    </Box>
  );
}
